import React, { useState, useEffect } from 'react';
import { useBusinessContext } from '../contexts/BusinessContext';
import { businessApi, CryptoPaymentNetwork, CryptoPaymentAddress, CryptoPaymentTip, type CreateOrderRequest } from '../services/businessApi';
import { emitOpenOrdersDialog } from '../utils/ordersEvents';
import './PaymentDialog.css';

type PaymentDetailsPayload = {
  payment_network_id: string;
  payment_network_name: string;
  payment_network_symbol: string;
  payment_network_chain: string;
  payment_wallet_address: string;
  payment_tx_hash: string;
};
export function PaymentDialog() {
  const {
    isPaymentDialogOpen,
    closePaymentDialog,
    businessDetails,
    openOrderDetailsDialog,
    submitBusinessData,
    closeAllDialogs,
    setBusinessDetails
  } = useBusinessContext();

  const [selectedProtocol, setSelectedProtocol] = useState<string>('');
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cryptoNetworks, setCryptoNetworks] = useState<CryptoPaymentNetwork[]>([]);
  const [cryptoAddresses, setCryptoAddresses] = useState<CryptoPaymentAddress[]>([]);
  const [cryptoTips, setCryptoTips] = useState<CryptoPaymentTip[]>([]);
  const [loadingCrypto, setLoadingCrypto] = useState(true);
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [txHash, setTxHash] = useState('');
  const [txHashError, setTxHashError] = useState('');
  const [showTxConfirm, setShowTxConfirm] = useState(false);

  // Fetch crypto payment data when dialog opens
  useEffect(() => {
    if (isPaymentDialogOpen) {
      loadCryptoPaymentData();
      // Reset payment state
      setTxHash('');
      setTxHashError('');
      setPaymentConfirmed(false);
      setShowTxConfirm(false);
    }
  }, [isPaymentDialogOpen]);

  // Generate QR code when selected protocol or addresses change
  useEffect(() => {
    if (selectedProtocol && cryptoAddresses?.length > 0) {
      const addresses = getAddressesForSelectedNetwork();
      if (addresses.length > 0) {
        generateQRCode(addresses[0].address);
      }
    }
  }, [selectedProtocol, cryptoAddresses]);

  const loadCryptoPaymentData = async () => {
    try {
      setLoadingCrypto(true);
      const response = await businessApi.getCompletePaymentDetails();

      if (response.success && response.data) {
        const data = response.data;
        setCryptoNetworks(data.networks || []);
        setCryptoAddresses(data.addresses || []);
        setCryptoTips(data.tips || []);

        // Set default selected protocol to first available network
        if (data.networks && data.networks.length > 0) {
          setSelectedProtocol(data.networks[0].id);
        }
      } else {
        console.error('Failed to load payment details:', response.message);
      }
    } catch (error) {
      console.error('Failed to load crypto payment data:', error);
    } finally {
      setLoadingCrypto(false);
    }
  };

  const handleBackToOrderDetails = () => {
    closePaymentDialog();
    openOrderDetailsDialog();
  };

  const handleConfirm = async (details: PaymentDetailsPayload) => {
    if (!businessDetails) return;
    setIsLoading(true);
    setTxHashError('');

    try {
      if (businessDetails.orderId) {
        const updateResult = await businessApi.updateOrder(businessDetails.orderId, details);
        if (!updateResult.success) {
          throw new Error(updateResult.message || 'Failed to update payment details');
        }
      } else {
        await submitBusinessData(details);
      }
      setPaymentConfirmed(true);
      closeAllDialogs();
      emitOpenOrdersDialog();
    } catch (error) {
      console.error('Payment processing error:', error);
      setTxHashError(
        error instanceof Error ? error.message : 'Failed to submit payment details. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const buildPaymentOverrides = (): PaymentDetailsPayload | null => {
    const network = getSelectedNetwork();
    const addresses = getAddressesForSelectedNetwork();
    if (!network || addresses.length === 0) {
      return null;
    }
    const primaryAddress = addresses[0];
    const trimmedHash = txHash.trim();
    if (!trimmedHash) {
      return null;
    }
    return {
      payment_network_id: network.id,
      payment_network_name: network.name,
      payment_network_symbol: network.symbol,
      payment_network_chain: network.network,
      payment_wallet_address: primaryAddress.address,
      payment_tx_hash: trimmedHash,
    };
  };

  const handleRequestConfirmation = () => {
    if (loadingCrypto || !selectedProtocol) return;
    if (getAddressesForSelectedNetwork().length === 0) {
      setTxHashError('No wallet address available for the selected network.');
      return;
    }
    if (!txHash.trim()) {
      setTxHashError('Please paste the blockchain TXID hash for your payment.');
      return;
    }
    setTxHashError('');
    setShowTxConfirm(true);
  };

  const finalizeConfirmation = async () => {
    if (!businessDetails) return;
    const overrides = buildPaymentOverrides();
    if (!overrides) {
      setTxHashError('Unable to resolve payment details. Please re-check your selection.');
      setShowTxConfirm(false);
      return;
    }

    setBusinessDetails({
      ...businessDetails,
      paymentNetworkId: overrides.payment_network_id,
      paymentNetworkName: overrides.payment_network_name,
      paymentNetworkSymbol: overrides.payment_network_symbol,
      paymentNetworkChain: overrides.payment_network_chain,
      paymentWalletAddress: overrides.payment_wallet_address,
      paymentTxHash: overrides.payment_tx_hash,
    });

    setShowTxConfirm(false);
    await handleConfirm(overrides);
  };

  const handleCancel = () => {
    closePaymentDialog();
  };

  const handleCloseAfterPayment = () => {
    // Close all dialogs to ensure clean state
    closeAllDialogs();
    // Reset payment state for next time
    setPaymentConfirmed(false);
    setIsLoading(false);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const generateQRCode = async (address: string) => {
    try {
      // Simple QR code generation using a public API
      // In production, you might want to use a library like qrcode
      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(address)}`;
      setQrCodeDataUrl(qrCodeUrl);
    } catch (error) {
      console.error('Failed to generate QR code:', error);
    }
  };

  const getSelectedNetwork = () => {
    return cryptoNetworks?.find(network => network.id === selectedProtocol);
  };

  const getAddressesForSelectedNetwork = () => {
    return cryptoAddresses?.filter(address => address.network_id === selectedProtocol) || [];
  };

  const getTipsForSelectedNetwork = () => {
    return cryptoTips?.filter(tip => tip.network_id === selectedProtocol) || [];
  };

  if (!isPaymentDialogOpen || !businessDetails) return null;

  const getTotalPrice = () => {
    if (businessDetails.selectedPricingPlan?.priceDisplay) {
      return businessDetails.selectedPricingPlan.priceDisplay;
    }
    if (businessDetails.selectedPricingPlan?.setupFee?.amount) {
      return businessDetails.selectedPricingPlan.setupFee.amount;
    }
    if (businessDetails.selectedPricingPlan?.subscription?.amount) {
      return businessDetails.selectedPricingPlan.subscription.amount;
    }
    return businessDetails.selectedPlan === 'Standard Plan' ? '999' : '1999';
  };

  const getSelectedPlanName = () => {
    return businessDetails.selectedPricingPlan?.name || businessDetails.selectedPlan || 'N/A';
  };

  // Generate a random order number (in real app, this would come from backend)
  const orderNumber = 'FF' + Date.now().toString().slice(-6) + Math.random().toString(36).substr(2, 9).toUpperCase();

  return (
    <div className="payment-dialog-overlay" onClick={closePaymentDialog}>
      <div className="payment-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="payment-dialog-header">
          <button className="payment-dialog-back" onClick={handleBackToOrderDetails}>
            ←
          </button>
          <button className="payment-dialog-close" onClick={closePaymentDialog}>
            ×
          </button>
        </div>

        <div className="payment-dialog-content">
          <h2 className="payment-dialog-title">Pay</h2>

          {!paymentConfirmed ? (
            <div className="payment-dialog-form">
              {/* Order Number */}
              <div className="payment-form-group">
                <label className="payment-form-label">Order:</label>
                <div className="payment-order-number">{orderNumber}</div>
              </div>

              {/* Selected Plan */}
              <div className="payment-form-group">
                <label className="payment-form-label">Selected Plan:</label>
                <div className="payment-order-number">{getSelectedPlanName()}</div>
              </div>

              {/* Amount */}
              <div className="payment-form-group">
                <label className="payment-form-label">Amount:</label>
                <div className="payment-amount">{getTotalPrice()}</div>
              </div>

              {/* Protocol Selection */}
              <div className="payment-form-group">
                <label className="payment-form-label">Select Payment Network:</label>
                {loadingCrypto ? (
                  <div className="payment-loading">
                    <span>Loading networks...</span>
                  </div>
                ) : (
                  <div className="payment-networks">
                    {cryptoNetworks.length === 0 ? (
                      <div className="payment-empty">No networks available. Please try again later.</div>
                    ) : (
                      cryptoNetworks.map((network) => (
                        <button
                          key={network.id}
                          className={`payment-network-button ${selectedProtocol === network.id ? 'payment-network-selected' : ''
                            }`}
                          onClick={() => setSelectedProtocol(network.id)}
                        >
                          <div className="payment-network-info">
                            <span className="payment-network-name">{network.name}</span>
                            <span className="payment-network-details">
                              {network.symbol} • {network.network}
                            </span>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Crypto Address Section */}
              {selectedProtocol && !loadingCrypto && (
                <div className="payment-form-group">
                  <label className="payment-form-label">
                    Payment Address for {getSelectedNetwork()?.name}:
                  </label>
                  {getAddressesForSelectedNetwork().map((address, index) => (
                    <div key={address.id} className="payment-address-item">
                      <div className="payment-address-container">
                        <div className="payment-address-text">{address.address}</div>
                        <button
                          className="payment-copy-button"
                          onClick={() => copyToClipboard(address.address)}
                          title="Copy address"
                        >
                          {copiedAddress ? '✓' : '📋'}
                        </button>
                      </div>
                      {getAddressesForSelectedNetwork().length > 1 && (
                        <p className="payment-address-index">Address {index + 1}</p>
                      )}

                      {/* QR Code Section */}
                      {index === 0 && qrCodeDataUrl && (
                        <div className="payment-qr-section">
                          <div className="payment-qr-container">
                            <img
                              src={qrCodeDataUrl}
                              alt="Payment QR Code"
                              className="payment-qr-image"
                            />
                          </div>
                          <p className="payment-qr-hint">
                            Scan QR code with your crypto wallet to send payment
                          </p>
                          <button
                            className="payment-qr-copy-button"
                            onClick={() => copyToClipboard(address.address)}
                            title="Copy address"
                          >
                            📋 Copy Address
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  {getAddressesForSelectedNetwork().length === 0 && (
                    <div className="payment-warning">
                      ⚠️ No payment address available for this network. Please contact support.
                    </div>
                  )}
                </div>
              )}

              {/* Tips Section */}
              <div className="payment-tips">
                <h4 className="payment-tips-title">Important Information:</h4>
                <ol className="payment-tips-list">
                  <li>As the payment address is changed regularly, please make payment on the current page. Do not save the address for future payments, otherwise you will bear the loss.</li>
                  <li>If you encounter problems, please contact customer service.</li>
                  {/* {getTipsForSelectedNetwork().length > 0 && (
                    <li>
                      <strong>Minimum Payment:</strong> {getTipsForSelectedNetwork().map(tip => `${tip.amount} ${tip.currency}`).join(', ')} for {getSelectedNetwork()?.name} ({getSelectedNetwork()?.network})
                    </li>
                  )} */}
                  <li>Please ensure you send the exact amount: <strong>{getTotalPrice()}</strong></li>
                  <li>Double-check the payment address before sending your transaction</li>
                </ol>
              </div>

              {/* TX Hash Input */}
              <div className="payment-form-group">
                <label className="payment-form-label">Transaction Hash (TXID)</label>
                <input
                  type="text"
                  className="payment-input"
                  placeholder="Paste your blockchain transaction hash"
                  value={txHash}
                  onChange={(e) => {
                    setTxHash(e.target.value);
                    if (txHashError) {
                      setTxHashError('');
                    }
                  }}
                />
                <p className="payment-input-hint">
                  We match your payment using this hash. Double-check before submitting.
                </p>
                {txHashError && <p className="payment-error">{txHashError}</p>}
              </div>

              {/* Action Buttons */}
              <div className="payment-actions">
                <button
                  onClick={handleRequestConfirmation}
                  disabled={isLoading || !selectedProtocol || loadingCrypto}
                  className={`payment-confirm-button ${isLoading || !selectedProtocol || loadingCrypto ? 'payment-confirm-disabled' : ''}`}
                >
                  {isLoading ? 'Processing...' : 'Confirm'}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isLoading}
                  className="payment-cancel-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            /* Success Message */
            <div className="payment-success">
              <div className="payment-success-icon">✓</div>
              <h3 className="payment-success-title">Order Confirmed!</h3>
              <p className="payment-success-message">
                Your order has been successfully placed. Please wait for payment confirmation.
                Check the Orders section for updates or chat with our support assistant anytime.
              </p>

              <div className="payment-success-order">
                <p className="payment-success-order-label">Order ID:</p>
                <p className="payment-success-order-id">{businessDetails?.orderId}</p>
                <p className="payment-success-order-hint">
                  Please save this order ID for your records
                </p>
              </div>

              <button
                onClick={handleCloseAfterPayment}
                className="payment-success-close-button"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>

      {showTxConfirm && (
        <div className="payment-confirm-overlay">
          <div className="payment-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Verify Payment Details</h3>
            <p>Please confirm the network and TXID hash before submitting.</p>
            <div className="payment-confirm-details">
              <div>
                <span>Network:</span>
                <strong>{getSelectedNetwork()?.name}</strong>
              </div>
              <div>
                <span>Wallet Address:</span>
                <strong>{getAddressesForSelectedNetwork()[0]?.address || 'N/A'}</strong>
              </div>
              <div>
                <span>TX Hash:</span>
                <strong className="payment-confirm-hash">{txHash.trim()}</strong>
              </div>
            </div>
            <div className="payment-confirm-actions">
              <button className="payment-cancel-button" onClick={() => setShowTxConfirm(false)}>
                Review Again
              </button>
              <button className="payment-confirm-button" onClick={finalizeConfirmation}>
                Submit Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

